// Server-verified Support Desk progress (phase 1), real accounts only.
//
// The learner record (localStorage + learner_state) keeps working exactly as before for the
// Support Desk flow itself: unlocking, drafts, the celebration. What counts for REWARDS — XP, the
// Support Desk Medal, Your week — comes from public.completions, which only the verify-support
// Edge Function can write after replaying the learner's commands and re-running the checks.
//
//   load()   read this account's verified ids (after sign-in / on boot)
//   check()  send the local Support Desk record for verification when it has a completion the
//            server has not confirmed yet (called after every Support Desk change, debounced)
//   view     a read-only support-progress adapter for reward code (summary / ticketRecord /
//            practiceRecord) that only reports server-verified tickets as complete
import { SUPPORT_TICKETS, PRACTICE_TICKET } from '../domains/support/tickets.js';

const IDS = [PRACTICE_TICKET.id, ...SUPPORT_TICKETS.map((t) => t.id)];
/** Server outcomes that mean "this saved work can't be checked — do the ticket again". */
const REDO_OUTCOMES = new Set(['updated-restart', 'not-validated-draft']);

/**
 * @param {{ client: object, store: object, local: object, userId: () => (string|null), debounceMs?: number }} o
 *   client: supabase-js client · store: learner store · local: learner createSupportProgress
 */
export function createVerifiedSupport({ client, store, local, userId, debounceMs = 600 }) {
  let ids = new Set();
  let at = {};
  let reasons = {}; // id → outcome from the last server check
  let loadedFor = null;
  let status = 'idle'; // idle | loading | checking | ok | offline
  let timer = null;
  let running = null;
  const listeners = new Set();
  const emit = (type, extra = {}) => { for (const f of listeners) { try { f({ type, ...extra }); } catch { /* listener errors never break sync */ } } };
  const setRows = (rows) => {
    ids = new Set(); at = {};
    for (const r of rows || []) if (IDS.includes(r.item_id)) { ids.add(r.item_id); at[r.item_id] = Date.parse(r.verified_at) || Date.now(); }
  };
  const localDone = () => {
    local.reload();
    const out = SUPPORT_TICKETS.filter((t) => local.ticketRecord(t.id).status === 'complete' && local.ticketRecord(t.id).validated !== false).map((t) => t.id);
    if (local.practiceRecord().status === 'complete') out.unshift(PRACTICE_TICKET.id);
    return out;
  };
  const pending = () => localDone().filter((id) => !ids.has(id));

  async function load() {
    const uid = userId();
    if (!uid || !client) return false;
    status = 'loading';
    const { data, error } = await client.from('completions').select('item_id, kind, verified_at').eq('user_id', uid);
    if (userId() !== uid) return false; // account changed meanwhile
    if (error) { status = 'offline'; emit('status'); return false; }
    setRows(data); loadedFor = uid; status = 'ok';
    emit('loaded');
    return true;
  }

  async function check() {
    const uid = userId();
    if (!uid || !client) return null;
    if (loadedFor !== uid && !(await load())) return null;
    if (!pending().length) return { newly: [] };
    if (running) return running;
    const p = (async () => {
      status = 'checking'; emit('status');
      try {
        const { data, error } = await client.functions.invoke('verify-support', { body: { track: store.get('support-track', {}) } });
        if (userId() !== uid) return null;
        if (error || !data || !Array.isArray(data.verified)) { status = 'offline'; emit('status'); return null; }
        const before = new Set(ids);
        const now = Date.now();
        reasons = {};
        for (const r of Array.isArray(data.results) ? data.results : []) if (r && IDS.includes(r.id)) reasons[r.id] = String(r.outcome || '');
        for (const id of data.verified) if (IDS.includes(id)) { ids.add(id); if (!at[id]) at[id] = now; }
        status = 'ok';
        const newly = [...ids].filter((id) => !before.has(id));
        emit('verified', { newly });
        return { newly };
      } catch { status = 'offline'; emit('status'); return null; }
    })();
    running = p; // cleared outside the body (see src/core/verifiedLabs.js)
    p.finally(() => { if (running === p) running = null; });
    return p;
  }

  const off = store.onChange?.((key) => {
    if (key !== 'support-track' || !userId()) return;
    clearTimeout(timer); timer = setTimeout(() => { check(); }, debounceMs);
  });

  const view = {
    summary() {
      const done = SUPPORT_TICKETS.filter((t) => ids.has(t.id)).length;
      return { done, total: SUPPORT_TICKETS.length, complete: done === SUPPORT_TICKETS.length };
    },
    ticketRecord(id) {
      if (!ids.has(id)) return {};
      return { ...local.ticketRecord(id), status: 'complete', validated: true, test: false, bypass: false, completedAt: at[id] };
    },
    practiceRecord() {
      if (!ids.has(PRACTICE_TICKET.id)) return {};
      return { ...local.practiceRecord(), status: 'complete', validated: true, test: false, bypass: false, completedAt: at[PRACTICE_TICKET.id] };
    },
  };

  return {
    view,
    load, check,
    isVerified: (id) => ids.has(id),
    /**
     * 'verified' · 'checking' · 'redo' (the server could not check this saved work — do it again)
     * · 'waiting' (checked once an earlier ticket is verified) · 'unverified' · null (not complete locally)
     */
    stateOf(id) {
      const done = id === PRACTICE_TICKET.id ? local.practiceRecord().status === 'complete' : local.ticketRecord(id).status === 'complete';
      if (ids.has(id)) return 'verified';
      if (!done) return null;
      if (status === 'checking' || status === 'loading' || loadedFor !== userId()) return 'checking';
      if (REDO_OUTCOMES.has(reasons[id])) return 'redo';
      if (this.blockerOf(id)) return 'waiting';
      return 'unverified';
    },
    /** The first earlier Support Desk ticket that is not verified yet: { id, number } or null. */
    blockerOf(id) {
      const i = SUPPORT_TICKETS.findIndex((t) => t.id === id);
      for (let k = 0; k < i; k++) if (!ids.has(SUPPORT_TICKETS[k].id)) return { id: SUPPORT_TICKETS[k].id, number: k + 1 };
      return null;
    },
    get status() { return status; },
    get loadedFor() { return loadedFor; },
    pending,
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    /** Sign-out / account switch: forget the previous account's verified ids. */
    reset() { clearTimeout(timer); ids = new Set(); at = {}; reasons = {}; loadedFor = null; status = 'idle'; emit('reset'); },
    destroy() { off?.(); clearTimeout(timer); listeners.clear(); },
  };
}
